import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PartyPopper, Copy, Share2 } from "lucide-react";

// "Bring a friend" prompt (2026-02): one-time warm nudge after a user's FIRST
// successful application. Shown-state persists server-side via the celebration
// ack pattern (users.celebrated_achievements: "prompt_first_application"), so
// it fires once ever regardless of device. Two display paths, never both:
//  - FirstApplicationPromptHost (global): the moment an apply response reports
//    first_application (OpportunityDetail / Discover — no celebration modal
//    exists on those pages, so no stacking is possible).
//  - CelebrationHost queue (Dashboard): if the in-the-moment prompt was missed
//    (tab closed etc.), the server appends it AFTER fresh achievements in the
//    one-at-a-time queue.
export const BringAFriendDialog = ({ onClose }) => {
  const [refInfo, setRefInfo] = useState(null);

  useEffect(() => {
    api.myReferral().then((d) => setRefInfo(d || {})).catch(() => setRefInfo({}));
    // ack on display (celebration pattern) — a reload never re-fires it
    api.ackCelebrations(["prompt_first_application"]).catch(() => {});
  }, []);

  const url = refInfo?.referral_url;
  const message = url ? `I just signed up to volunteer on CivicHearth — join me: ${url}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Invite message copied — paste it anywhere");
      api.trackReferralShare("prompt").catch(() => {});
    } catch {
      toast.error("Couldn't copy the invite");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on CivicHearth",
          text: "I just signed up to volunteer on CivicHearth — join me:",
          url,
        });
        api.trackReferralShare("prompt").catch(() => {});
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user closed the sheet
      }
    }
    copy(); // no share sheet on this device — copy is the fallback
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="bring-friend-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-bold flex items-center gap-1.5">
            <PartyPopper className="w-4 h-4" /> First application in
          </div>
          <DialogTitle data-testid="bring-friend-title" className="font-display text-3xl text-forest tracking-tight">
            You just took your first step!
          </DialogTitle>
          <DialogDescription data-testid="bring-friend-desc">
            Brilliant — your application is on its way. Volunteering is twice the fun
            with company, and you'll both watch the impact add up.
          </DialogDescription>
        </DialogHeader>

        {url && (
          <div data-testid="bring-friend-message" className="rounded-xl border border-forest/15 bg-forest/5 px-4 py-3 text-sm text-forest break-all">
            {message}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {url && (
            <>
              <Button
                data-testid="bring-friend-share-btn"
                onClick={share}
                className="rounded-full bg-forest hover:bg-forest-hover text-white h-10 px-5"
              >
                <Share2 className="w-4 h-4 mr-1.5" /> Bring a friend along
              </Button>
              <Button
                data-testid="bring-friend-copy-btn"
                variant="outline"
                onClick={copy}
                className="rounded-full h-10 border-forest/20 text-forest hover:bg-forest/5"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copy invite
              </Button>
            </>
          )}
          <Button
            data-testid="bring-friend-dismiss-btn"
            variant="ghost"
            onClick={onClose}
            className="rounded-full ml-auto text-neutral-500 hover:text-neutral-700"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Global listener: apply flows dispatch "civic:first-application" when the
// backend confirms this was the user's first-ever application.
export const FirstApplicationPromptHost = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let t;
    const onFirst = () => { t = setTimeout(() => setOpen(true), 900); }; // let the success toast land first
    window.addEventListener("civic:first-application", onFirst);
    return () => { clearTimeout(t); window.removeEventListener("civic:first-application", onFirst); };
  }, []);

  if (!open) return null;
  return <BringAFriendDialog onClose={() => setOpen(false)} />;
};
